import AppConfigModel from "../models/app-config";
import DealerModel from "../models/dealer";
import { setAppConfig, setAuthRemember, setAuthToken, setAuthDealer, setFormErrors, setLoading } from "../redux/actions";
import { LzAppConstants } from "./Constants";
import { lzGetTimestamp } from "./LzMoment";
import { lzIsArray, lzIsObject, lzJsonToString } from "./LzType";
import { lzLogInfo, lzDownloadBlobFile, lzShowErrorMessage } from "./LzUtils";

const lzBuildAndCallApi = async (action, params, method) => {

    // Get state and dispatch from redux store
    const reduxState = window.reduxStore.getState();

    // Init based on action type
    let initData = {};
    let url = LzAppConstants.app_api_endpoint_url + action;

    // Setup params
    method = method ? method : 'get';
    params = params ? params : {};
    params.timestamp = lzGetTimestamp();

    // Setup headers
    const headers = {};
    if (reduxState.auth && reduxState.auth.token) {
        headers['Authorization'] = 'Bearer ' + reduxState.auth.token;
    }

    // Make call params in case of get request
    if ('get' === method) {
        // Edit the url with query string
        url += (url.indexOf('?') < 0 ? '?' : '&') + Object.entries(params).map((p) => p[0] + '=' + encodeURIComponent(p[1])).join('&');

        // Make init data
        initData = {
            method: 'GET',
            headers,
        };
    }

    // In-case of anything else, we take it as POST request
    else {
        // Set data to send for api call
        const data = new FormData();
        if (lzIsObject(params)) {
            for (const key in params) {
                // Check for Blob type object and perform special treatment and skip loop
                if (params[key] && typeof params[key].arrayBuffer === 'function') {
                    data.append(key, params[key], params[key].filename);
                    continue;
                }

                data.append(
                    key,
                    lzIsObject(params[key]) || lzIsArray(params[key]) ? lzJsonToString(params[key]) : params[key]
                );
            }
        }

        // Set the method (PUT, PATCH, etc) as post _method so that backend handles it accordingly
        data.append('_method', method.toUpperCase());

        // Make init data
        initData = {
            method: 'POST',
            body: data,
            headers,
        };
    }

    // Call the api with await
    try {
        lzLogInfo('API CALL: ', url, initData);
        const response = await fetch(url, initData);
        lzLogInfo('API RESP: ', response);

        return response;
    } catch (e) {
        console.log(e);
        return null;
    }
};

export const lzCallApi = async (action, params, method, doNotCleanFormErrors, doNotShowLoading) => {
    // Get state and dispatch from redux store
    const reduxState = window.reduxStore.getState();
    const reduxDispatch = window.reduxStore.dispatch;

    // NOTE: We do not skip if loading because then it skips simultaneous calls.
    //       If ever simultaneous calls create a problem, then we will have to 
    //       look for an ajax call handler that supports multiple calls at the
    //       same time.

    // Tell app that we are loading
    if (!doNotShowLoading) {
        reduxDispatch(setLoading(true));
    }

    // If not prohibited to clean form errors, then clean them (handy if you are making an intermediate api call and do not want form errors to go away)
    if (!doNotCleanFormErrors) {
        reduxDispatch(setFormErrors(null));
    }

    // Make the api call and get processed data
    const response = await lzBuildAndCallApi(action, params, method);

    if (!response) {
        if (!doNotShowLoading) {
            reduxDispatch(setLoading(false));
        }
        lzShowErrorMessage('Api request cannot be made...');
        return null;
    }

    // console.log('Content-Type:', response.headers.get("Content-Type"));

    // If response code is 403 then it means that authentication is failed and dealer should be logged out
    if (403 === response.status) {
        console.error('DEALER LOGGED OUT! Revoking token and sending to login page...');
        reduxDispatch(setAuthDealer(null));
        reduxDispatch(setAuthToken(''));
        if (!doNotShowLoading) {
            reduxDispatch(setLoading(false));
        }
        // lzShowErrorMessage('please login to continue...');
        return null;
    }

    // console.log('CALL', action, response.type, response.headers.get('Content-Type'), response.headers.get('Content-Disposition'));

    // If response is a csv then show it
    const responseContentType = response?.headers?.get('Content-Type');
    if (responseContentType?.indexOf('text/csv') >= 0) {
        const filename = response.headers.get('Content-Disposition')?.replace('attachment; filename=', '') || 'export.csv';
        response.blob().then((blobData) => lzDownloadBlobFile(blobData, filename));
        if (!doNotShowLoading) {
            reduxDispatch(setLoading(false));
        }
        return null;
    }
    if (responseContentType?.indexOf('application/pdf') >= 0) {
        const filename = response.headers.get('Content-Disposition')?.replace('attachment; filename="', '')?.replace('"', '') || 'report.pdf';
        response.blob().then((blobData) => lzDownloadBlobFile(blobData, filename));
        if (!doNotShowLoading) {
            reduxDispatch(setLoading(false));
        }
        return null;
    }

    // Parse the json out of the response
    try {
        const json = await response.json();
        lzLogInfo('API DATA: ', json);

        // Handle the data
        if (!json || typeof json.data === 'undefined' || typeof json.message === 'undefined') {
            lzShowErrorMessage(json.message || 'Invalid data returned from server.');
            reduxDispatch(setLoading(false));
            return null;
        }

        // Hide loading
        if (!doNotShowLoading) {
            reduxDispatch(setLoading(false));
        }

        // Validate success and return it if valid
        if (json.success) {
            // Check if logged in data needs to be updated
            lzParseLoggedInData(json.logged_in_data, reduxState.auth.remember);

            // Return the api call data
            return json.data;
        }

        /*let errorMessage = json.message;// + '<pre>';
        const keys = Object.keys(json.data);
        for (const key of keys) {
            for (const msg of json.data[key]) {
                errorMessage += "\n\t- " + msg;
            }
        }
        // errorMessage += '</pre>';

        lzShowErrorMessage(errorMessage);*/

        lzShowErrorMessage(json.message);
        reduxDispatch(setFormErrors(json.data));

        return null;
    } catch (e) {
        console.error(e.toString());
        if (!doNotShowLoading) {
            reduxDispatch(setLoading(false));
        }
        lzShowErrorMessage('An error has occured in the api call to server!<br/>Please check logs for details.');
        return null;
    }
};

export const lzParseLoggedInData = (data, remember) => {
    // Get dispatch from redux store
    const reduxDispatch = window.reduxStore.dispatch;

    // console.log('lzParseLoggedInData()', data, remember);

    // If data is not a valid object then skip
    if (!data) {
        return false;
    }

    // If data has token and claims then set them
    if (data.token && data.claims) {
        reduxDispatch(setAuthToken(data.token, remember));
        reduxDispatch(
            setAuthDealer(
                new DealerModel(
                    {
                        ...data.claims,
                        is_ffl_approved: !!data.is_ffl_approved,
                        is_subscription_active: !!data.is_subscription_active,
                    }
                ),
                remember
            )
        );
        reduxDispatch(setAppConfig(new AppConfigModel(data.app_config)));
    }

    // Save remember flag
    reduxDispatch(setAuthRemember(remember));

    return true;
};
